import { useEffect, useState } from 'react';

/**
 * True once every URL in `srcs` has downloaded AND decoded — the readiness
 * gate for entrance choreography (a reveal that plays over unloaded art
 * spends its one entrance on an empty frame; see the Reveal wrapper in
 * HomePage and the DiamondTravel `active` gates).
 *
 * Uses Image() + decode(), which shares the browser's HTTP cache with the
 * real <img> elements, so already-cached art resolves in the same tick and
 * warm visits are never delayed. `timeoutMs` is the safety valve: on a
 * stalled connection the gate opens anyway so choreography can never be
 * held hostage by one asset.
 *
 * `srcs` is keyed by value — inline array literals are fine.
 */
export function useArtReady(srcs: readonly string[], timeoutMs = 4000): boolean {
  const [ready, setReady] = useState(srcs.length === 0);
  const key = srcs.join('|');

  useEffect(() => {
    if (!key) {
      setReady(true);
      return;
    }
    let cancelled = false;
    const finish = () => {
      if (!cancelled) setReady(true);
    };
    setReady(false);
    const timer = window.setTimeout(finish, timeoutMs);
    void Promise.allSettled(
      key.split('|').map((src) => {
        const img = new Image();
        img.src = src;
        // decode() also rejects on broken art — allSettled treats that as
        // "done" rather than blocking the page on a bad asset.
        return img.decode();
      }),
    ).then(finish);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [key, timeoutMs]);

  return ready;
}
